import React, { useState, useContext, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import { FaFootballBall, FaTrophy, FaUser, FaSignInAlt, FaUserPlus, FaKey, FaComments, FaChartLine } from 'react-icons/fa'; // Added FaComments
import SignUp from './components/SignUp';
import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import GameList from './components/GameList';
import UserProfile from './components/UserProfile';
import Leaderboard from './components/Leaderboard';
import Messaging from './components/Messaging'; // Import Messaging component
import AuthContext from './AuthContext';
import ResetPassword from './components/ResetPassword';
import StatsPage from './components/StatsPage';
import { ref, onValue } from 'firebase/database';
import { database } from './firebaseConfig';
import { useAutoLockService } from './hooks/useAutoLockService';
import './tailwind.css';

const App = () => {
  const { currentUser } = useContext(AuthContext);
  const [isAdmin, setIsAdmin] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    if (currentUser) {
      const userRef = ref(database, `users/${currentUser.uid}`);
      onValue(userRef, (snapshot) => {
        const data = snapshot.val();
        setIsAdmin(data?.admin || false);
        setName(data?.name || '');
      }, (error) => {
        console.error('App: Error fetching user data:', error);
      });
    } else {
      console.log('App: No currentUser');
    }
  }, [currentUser]);

  return (
    <Router>
      <AppContent currentUser={currentUser} isAdmin={isAdmin} name={name} />
    </Router>
  );
};

const NavItem = ({ to, icon: Icon, label, active }) => (
  <Link
    to={to}
    className={`flex flex-col items-center gap-1 min-w-0 px-1 ${active ? 'text-yellow-500' : 'text-accent hover:text-gray-200'}`}
  >
    <Icon size={22} />
    <span className="text-[10px] leading-none tracking-wide">{label}</span>
  </Link>
);

const AppContent = ({ currentUser, isAdmin, name }) => {
  const location = useLocation(); // Get the current location
  
  // Initialize the auto-lock service
  useAutoLockService();

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center dark">
      <div className="flex-1 pb-20 pt-4 w-full max-w-md overflow-y-auto">
        <Routes>
          {!currentUser ? (
            <>
              <Route path="/signup" element={<SignUp />} />
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<Login />} /> {/* Default route for non-logged in users */}
            </>
          ) : (
            <>
              <Route path="/profile" element={<UserProfile name={name} />} />
              <Route path="/games" element={<GameList />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/messaging" element={<Messaging />} /> {/* Added messaging route */}
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/stats" element={<StatsPage />} />
              {isAdmin && <Route path="/admin" element={<AdminPanel />} />}
              <Route path="/" element={<GameList />} /> {/* Default route for logged in users */}
            </>
          )}
        </Routes>
      </div>
      <nav className="bg-gray-800 px-2 pt-2 pb-3 fixed bottom-0 z-40 w-full max-w-md flex justify-around items-end">
        {currentUser ? (
          <>
            <NavItem to="/games" icon={FaFootballBall} label="Picks" active={location.pathname === '/games' || location.pathname === '/'} />
            <NavItem to="/leaderboard" icon={FaTrophy} label="Board" active={location.pathname === '/leaderboard'} />
            <NavItem to="/messaging" icon={FaComments} label="Chat" active={location.pathname === '/messaging'} />
            <NavItem to="/stats" icon={FaChartLine} label="Stats" active={location.pathname === '/stats'} />
            <NavItem to="/profile" icon={FaUser} label="You" active={location.pathname === '/profile'} />
          </>
        ) : (
          <>
            <NavItem to="/signup" icon={FaUserPlus} label="Sign Up" active={location.pathname === '/signup'} />
            <NavItem to="/login" icon={FaSignInAlt} label="Log In" active={location.pathname === '/login'} />
            <NavItem to="/reset-password" icon={FaKey} label="Reset" active={location.pathname === '/reset-password'} />
          </>
        )}
      </nav>
    </div>
  );
};

export default App;